import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { db } from '../firebase';
import { getDocs, doc, updateDoc } from 'firebase/firestore';
import { lastNameComparator } from '../utils/sortByLastName';
import { logAudit } from '../services/Logger';
import StudentDetailsModal from '../components/StudentDetailsModal';
import { isDocInSchoolYear, isQuarterInSchoolYear, schoolYearLabel } from '../utils/schoolYear';
import { resolveActiveSchoolYear } from '../utils/activeSchoolYear';
import SchoolYearNotice from '../components/SchoolYearNotice';
import { buildMyClassesQuery, getDocsByClassIds, schoolYearQuarterLabels } from '../utils/classroomQueries';
import { buildClassLabelMap, classLabelFrom } from '../utils/classDisplay';
import { usePageLoad, LOAD_TIMEOUT_MS } from '../hooks/usePageLoad';
import LoadErrorNotice from '../components/LoadErrorNotice';
import { useOnboardingScreen, TOUR_TARGETS, SCREEN_DASHBOARD } from '../onboarding';
import { isVowelTestType } from '../constants/vowelTests';
import { bandForScore, letterScaleNote, BAND_INDICATOR_CLASS, BAND_LABELS } from '../utils/bandForScore';
import {
  TEST_TYPE_PARAM,
  DEFAULT_LETTER_SOUND_TEST,
  letterSoundDocTestTypes,
  isOfferedLetterSoundTest,
  resolveLetterSoundTest,
  recallDashboardTestType,
  rememberDashboardTestType,
  rememberDashboardSoundsCase,
} from '../utils/assessmentEntry';
import {
  LETTER_SOUND_ID, ASSESSMENT_PARAM, PA_TEST_PARAM,
  dashboardAssessments, resolveDashboardAssessment, extraCollectionsFor,
  testOptionsFor, resolvePaTest, columnFor, headingFor,
  takeHrefFor, takeLabelFor, takeTitleFor, takeAriaLabelFor, resultsHrefFor,
  legendFor, percentLegendNote, latestByColumn, assessmentsColumns,
  recallDashboardAssessment, rememberDashboardAssessment, recallDashboardPaTest, rememberDashboardPaTest,
} from '../utils/dashboardAssessments';
import { readinessNotesFromDocs } from '../utils/studentNotes';
import { isGroupVisible } from '../utils/sidebarVisibility';
import { isFeatureEnabled } from '../constants/featureFlags';
import { BOY_GROUP_NAME } from '../constants/boyItems';
import { collectStudentNotes } from '../utils/studentNotesDigest';
import StudentNotesPeek from '../components/StudentNotesPeek';

// v3.97.0 — the Assessment picker (src/utils/dashboardAssessments.js):
// Letters & Sounds (its Test Type rows as before — the shared row table, so
// the selector, the Take mapping and the landing page agree; every Sounds row
// shows the same latest Sounds result, keyed by the doc's testType), High
// Frequency Words, CVC Words, Nonsense Word Fluency, and the two PA
// assessments where their switches are on. HFW / CVC results live in their
// own collections: they are read in the SAME round trip as students ∥
// assessments but never hold the page — the Letter/Sound view renders as
// before and an HFW / CVC view says "Loading…" until they land (15 s → a
// visible, retryable error). Budget row: docs/perf/PERFORMANCE.md.

// Band id for a latest score — the shared bandForScore (v3.78.0, pure
// refactor: bandForScore.test.js pins the identical key for every integer
// score, and the cuts are unchanged — letter tests Mastery 22–26 / Emerging
// 12–21 / Needs Support 0–11; vowels 5 / 3–4 / 0–2). v3.85.0: the doc's
// item total rides along so a 21-letter consonants-only run bands on its own
// scale (cuts 18 / 10 — fractions of 26); absent → 26, as before. v3.97.0:
// HFW / CVC band on 80% / 50% of the test's own word count, NWF on 21 / 14.
const bandIdFor = (testType, score, totalItems) => bandForScore(testType, score, totalItems)?.key ?? null;

// The class card's band panels, worst first (the v3.2 look, one style per band;
// the PA screener's two bands borrow Emerging's and Mastery's colours).
const PANEL_STYLES = {
  needs_support: { border: '#dc3545', heading: '#dc3545', row: '#fdf3f4', chip: '#dc3545', chipText: 'white', dot: 'radial-gradient(circle at 30% 30%, #ff8a8a, #dc3545)' },
  emerging: { border: '#ffc107', heading: '#d39e00', row: '#fff9e6', chip: '#ffc107', chipText: '#333', dot: 'radial-gradient(circle at 30% 30%, #ffe699, #ffc107)' },
  mastery: { border: '#28a745', heading: '#28a745', row: '#f4fcf6', chip: '#28a745', chipText: 'white', dot: 'radial-gradient(circle at 30% 30%, #85d697, #28a745)' },
};
PANEL_STYLES.developing = PANEL_STYLES.emerging;
PANEL_STYLES.proficient = PANEL_STYLES.mastery;

const COLLECTION_LABELS = { hfw_assessments: 'High Frequency Words', cvc_assessments: 'CVC Words' };
const listText = (names) => (names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`);

function Dashboard({ user }) {
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState({});
  const [allAssessments, setAllAssessments] = useState([]); // Track all assessments for the modal
  // HFW / CVC results (v3.97.0): read beside students ∥ assessments, never
  // holding the page. status: 'idle' | 'loading' | 'loaded' | 'failed'.
  const [extras, setExtras] = useState({ status: 'idle', hfw: [], cvc: [], collections: [] });
  const extrasRunRef = React.useRef(0);
  const extrasArgsRef = React.useRef(null);
  const [pendingAssessments, setPendingAssessments] = useState([]);
  const [schoolYearInfo, setSchoolYearInfo] = useState(null);
  // The resolved active school-year start year, readable inside fetch closures.
  const activeYearRef = React.useRef(null);
  const [selectedClassFilter, setSelectedClassFilter] = useState('All');
  // Test Type selection (v3.84.10): URL first (`?testType=`), then this
  // tab's remembered pick, then Uppercase — so Submit → Dashboard (Back or
  // the sidebar link) keeps the test the teacher is working through, and
  // Take Assessment carries it. An unknown or gated-off key reads as the
  // default. Component state still drives the render; ONLY an explicit
  // change writes the URL and the tab memory (handleTestTypeChange) — a
  // pick restored from memory leaves a bare /dashboard bare.
  const [params, setParams] = useSearchParams();
  const requestedTestType = params.get(TEST_TYPE_PARAM);
  const [selectedTestType, setSelectedTestType] = useState(() =>
    resolveLetterSoundTest(requestedTestType || recallDashboardTestType())
  );
  // Assessment selection (v3.97.0): the same URL → tab memory → default
  // order (`?assessment=hfw`; Letters & Sounds leaves the URL bare). One that
  // is not offered (switched off, hidden in Assessment Settings, hand-edited)
  // reads as Letters & Sounds. A PA assessment's test rides `?paTest=`.
  const offeredAssessments = dashboardAssessments(user?.profile);
  const requestedAssessment = params.get(ASSESSMENT_PARAM);
  const [selectedAssessmentId, setSelectedAssessmentId] = useState(() =>
    resolveDashboardAssessment(requestedAssessment || recallDashboardAssessment(), offeredAssessments).id
  );
  const assessment = resolveDashboardAssessment(selectedAssessmentId, offeredAssessments);
  const [paTestPicks, setPaTestPicks] = useState({});
  const paTest = assessment.tests
    ? resolvePaTest(assessment, paTestPicks[assessment.id]
      || (assessment.id === requestedAssessment ? params.get(PA_TEST_PARAM) : null)
      || recallDashboardPaTest(assessment.id))
    : null;
  // The result column the view shows — for Letters & Sounds the row's doc
  // testType ('sounds' for every Sounds row).
  const selectedColumn = columnFor(assessment, selectedTestType, paTest);
  const selectedHeading = headingFor(assessment, selectedTestType, paTest);
  const [selectedStudentForModal, setSelectedStudentForModal] = useState(null);

  // HFW / CVC: one read per collection, started in the same round trip as
  // students ∥ assessments (CLAUDE.md: max 3 sequential hops) and NOT awaited
  // by the page load. Own 15 s backstop; a late success after it self-heals;
  // a newer run (Retry, reload, unmount) makes an older one's result inert.
  const startExtras = useCallback((classIds, startYear, collections) => {
    const run = ++extrasRunRef.current;
    extrasArgsRef.current = { classIds, startYear, collections };
    if (collections.length === 0 || classIds.length === 0) {
      setExtras({ status: 'loaded', hfw: [], cvc: [], collections });
      return;
    }
    setExtras({ status: 'loading', hfw: [], cvc: [], collections });
    const timer = setTimeout(() => {
      if (extrasRunRef.current !== run) return;
      console.error(`HFW / CVC results still pending after ${LOAD_TIMEOUT_MS}ms — showing error state.`);
      setExtras((prev) => (prev.status === 'loading' ? { ...prev, status: 'failed' } : prev));
    }, LOAD_TIMEOUT_MS);
    Promise.all(collections.map((c) => getDocsByClassIds(c, classIds, { quarterLabels: schoolYearQuarterLabels(startYear) })))
      .then((snaps) => {
        if (extrasRunRef.current !== run) return;
        const byCollection = {};
        collections.forEach((c, i) => {
          byCollection[c] = snaps[i]
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((r) => isQuarterInSchoolYear(r.quarter, startYear));
        });
        setExtras({ status: 'loaded', hfw: byCollection.hfw_assessments || [], cvc: byCollection.cvc_assessments || [], collections });
      })
      .catch((err) => {
        console.error('Error fetching HFW / CVC results:', err);
        if (extrasRunRef.current === run) setExtras((prev) => ({ ...prev, status: 'failed' }));
      })
      .finally(() => clearTimeout(timer));
  }, []);
  useEffect(() => () => { extrasRunRef.current += 1; }, []);
  const retryExtras = () => {
    const args = extrasArgsRef.current;
    if (args) startExtras(args.classIds, args.startYear, args.collections);
  };

  const fetchDashboard = useCallback(async () => {
      if (!user) return;
      {
        // Which school year is active? Calendar-driven (district-specific first).
        const sy = await resolveActiveSchoolYear(user?.profile?.districtId);
        setSchoolYearInfo(sy);
        activeYearRef.current = sy.status === 'active' ? sy.startYear : null;
        if (sy.status !== 'active') return;

        // Role-aware query: pure-EA tokens use the assistantEmails clause only
        // (the or() shape is rules-denied without the teacher claim).
        const classesSnapshot = await getDocs(buildMyClassesQuery(user));
        const classesList = classesSnapshot.docs.filter(d => !d.data().deleted && isDocInSchoolYear(d.data(), activeYearRef.current)).map(doc => ({ id: doc.id, ...doc.data() }));
        classesList.sort((a, b) => a.id.localeCompare(b.id));
        setClasses(classesList);

        const classIds = classesList.map(c => c.id);

        // v3.97.0: HFW / CVC go out in this same hop (not awaited here).
        startExtras(classIds, activeYearRef.current, extraCollectionsFor(dashboardAssessments(user?.profile)));

        // Students stay client-filtered (bounded set); assessments are scoped
        // to the active school year's quarters SERVER-SIDE so prior years'
        // history is never downloaded (2026-07 audit, step 1b).
        //
        // The two are independent, so they go out together — this is the
        // "calendar → classes → students ∥ assessments = 3 round trips" shape
        // PERFORMANCE.md documents. It was written sequentially (4 hops) until
        // the 2026-07 perf audit, step 5.
        const [studentDocs, assessmentDocsRaw] = await Promise.all([
          getDocsByClassIds('students', classIds),
          getDocsByClassIds('assessments', classIds, {
            quarterLabels: schoolYearQuarterLabels(activeYearRef.current),
          }),
        ]);

        const studentsList = studentDocs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(s => isDocInSchoolYear(s, activeYearRef.current));

        const assessmentsDocs = assessmentDocsRaw
          .filter(d => isQuarterInSchoolYear(d.data().quarter, activeYearRef.current));

        const studentsByClass = studentsList.reduce((acc, student) => {
          if (!acc[student.classId]) acc[student.classId] = [];
          acc[student.classId].push(student);
          acc[student.classId].sort(lastNameComparator);
          return acc;
        }, {});
        setStudents(studentsByClass);

        const assessmentsDocsData = assessmentsDocs.map(doc => {
            const data = doc.data();
             if (data.timestamp && typeof data.timestamp.toDate === 'function') {
                data.timestamp = data.timestamp.toDate();
              }
            return { id: doc.id, ...data };
        });
        // The latest score per student and test is derived from these docs
        // (latestByColumn, below) together with the HFW / CVC results.
        setAllAssessments(assessmentsDocsData);

        // Populate pending assessments for the Review Queue
        const pending = assessmentsDocs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(a => a.status === 'pending_review');

        const pendingWithNames = pending.map(p => {
          const student = studentsList.find(s => s.id === p.studentId);
          return {
            ...p,
            studentName: student ? student.name : 'Unknown Student',
            className: p.classId ? p.classId.split('_')[0] : 'Unknown Class'
          };
        });
        setPendingAssessments(pendingWithNames);
      }
  }, [user, startExtras]);

  // Shared load state: errors (rules denial, missing index, offline) surface
  // as a retryable notice instead of a silent empty dashboard, and a load
  // hung on connectivity converts to the error state after 15s.
  const { loading, error: loadError, retry: retryLoad } = usePageLoad(fetchDashboard, [user], { enabled: !!user });

  // Onboarding tour readiness (v3.75.0): the class grid is rendered — loaded,
  // no error, active school year (the SchoolYearNotice early-return below is
  // NOT ready). Inert unless the onboardingTour flag is on.
  useOnboardingScreen(SCREEN_DASHBOARD, {
    ready: !loading && !loadError && !!schoolYearInfo && schoolYearInfo.status === 'active',
    classCount: classes.length,
  });

  useEffect(() => {
    document.title = "Early Literacy Assessment - Dashboard";
  }, []);

  // Back / Forward between two dashboard URLs: follow the URL's key.
  useEffect(() => {
    if (requestedTestType && isOfferedLetterSoundTest(requestedTestType)) {
      setSelectedTestType(requestedTestType);
    }
  }, [requestedTestType]);

  // The selector writes three places: state (render), the URL (refresh /
  // Back / shared link; the default pick clears the parameter so the bare
  // /dashboard stays canonical) and this tab's memory (the sidebar link).
  const handleTestTypeChange = (e) => {
    const value = e.target.value;
    setSelectedTestType(value);
    rememberDashboardTestType(value);
    // A Sounds row's case is remembered beside it (v3.85.0, flag on) — the
    // vowel cards and Practice's default inherit it. Non-Sounds picks leave it.
    rememberDashboardSoundsCase(value);
    const next = new URLSearchParams(params);
    if (value === DEFAULT_LETTER_SOUND_TEST) next.delete(TEST_TYPE_PARAM); else next.set(TEST_TYPE_PARAM, value);
    setParams(next, { replace: true });
  };

  // Back / Forward between two dashboard URLs: follow the URL's assessment.
  useEffect(() => {
    if (requestedAssessment && dashboardAssessments(user?.profile).some((a) => a.id === requestedAssessment)) {
      setSelectedAssessmentId(requestedAssessment);
    }
  }, [requestedAssessment, user?.profile]);

  // The Assessment selector: state, the URL (Letters & Sounds clears the
  // parameter; a PA test pick belongs to its assessment, so it goes too) and
  // this tab's memory. The Letter/Sound row pick is left as it is.
  const handleAssessmentChange = (e) => {
    const id = e.target.value;
    setSelectedAssessmentId(id);
    rememberDashboardAssessment(id);
    const next = new URLSearchParams(params);
    if (id === LETTER_SOUND_ID) next.delete(ASSESSMENT_PARAM); else next.set(ASSESSMENT_PARAM, id);
    next.delete(PA_TEST_PARAM);
    setParams(next, { replace: true });
  };

  // A PA assessment's own Test Type pick.
  const handlePaTestChange = (e) => {
    const value = e.target.value;
    setPaTestPicks((prev) => ({ ...prev, [assessment.id]: value }));
    rememberDashboardPaTest(assessment.id, value);
    const next = new URLSearchParams(params);
    next.set(PA_TEST_PARAM, value);
    setParams(next, { replace: true });
  };

  // Latest result per (column, student): the `assessments` docs (Letter/Sound,
  // NWF, PA) and the HFW / CVC results, one rule for all (latestByColumn).
  const latestAssessments = useMemo(() => {
    const columns = new Set(assessmentsColumns());
    const entries = allAssessments.filter((d) => columns.has(d.testType)).map((data) => ({ column: data.testType, data }));
    extras.hfw.forEach((data) => entries.push({ column: 'hfw', data }));
    extras.cvc.forEach((data) => entries.push({ column: 'cvc', data }));
    return latestByColumn(entries);
  }, [allAssessments, extras.hfw, extras.cvc]);

  // Whether the selected view's data is here: HFW / CVC wait for their read.
  const columnFromExtras = assessment.collection !== 'assessments';
  const columnReady = !columnFromExtras || extras.status === 'loaded';
  const extrasNames = listText(extras.collections.map((c) => COLLECTION_LABELS[c]).filter(Boolean));

  // Notes peek (v3.97.0): every note about each student — the student
  // record's teacher notes, the 4K Readiness note from the assessments
  // already read, and the HFW / CVC result notes once they land.
  // The 4K Readiness note follows that group's switch and the teacher's
  // Assessment Settings, like the HFW / CVC notes (hidden means hidden).
  const readinessShown = isFeatureEnabled('boyReadiness') && isGroupVisible(user?.profile, BOY_GROUP_NAME);
  const readinessNotes = useMemo(() => (readinessShown ? readinessNotesFromDocs(allAssessments) : {}), [allAssessments, readinessShown]);
  const extrasByStudent = useMemo(() => {
    const by = {};
    const add = (key, d) => {
      if (!d.studentId || typeof d.note !== 'string' || !d.note.trim()) return;
      if (!by[d.studentId]) by[d.studentId] = { hfw: [], cvc: [] };
      by[d.studentId][key].push(d);
    };
    extras.hfw.forEach((d) => add('hfw', d));
    extras.cvc.forEach((d) => add('cvc', d));
    return by;
  }, [extras.hfw, extras.cvc]);
  const notesFor = (student) => collectStudentNotes({
    studentId: student.id,
    studentDoc: student,
    readinessNote: readinessNotes[student.id],
    hfwDocs: extrasByStudent[student.id] ? extrasByStudent[student.id].hfw : [],
    cvcDocs: extrasByStudent[student.id] ? extrasByStudent[student.id].cvc : [],
  });
  const notesPendingText = extras.status === 'loading' && extrasNames
    ? `Loading notes from ${extrasNames}…`
    : extras.status === 'failed' && extrasNames
      ? `Notes from ${extrasNames} could not be loaded.`
      : null;

  const getIndicatorInfo = (score, testType = selectedColumn, totalItems = undefined) => {
    const band = bandIdFor(testType, score, totalItems);
    if (band === null) return { class: 'indicator-gray', label: 'Not Tested' };
    return { class: BAND_INDICATOR_CLASS[band] || 'indicator-gray', label: BAND_LABELS[band] || 'Not Tested' };
  };

  // Students of a class whose latest score for the selected test falls in a band.
  const studentsInBand = (studentList, bandId) => studentList.filter(s => {
    const latest = latestAssessments[selectedColumn] && latestAssessments[selectedColumn][s.id];
    return latest && bandIdFor(selectedColumn, latest.score, latest.totalItems) === bandId;
  });

  // Legend for the selected test, best band first: 26-point for the letter
  // tests, 5-point for a vowel / PA test, 25 for NWF, percent for HFW / CVC.
  // A consonants-only run (21 letters) keeps the legend on the 26 scale and
  // adds one line with its own cuts — only when such a result is on the page.
  const legend = legendFor(selectedColumn);
  const panelBands = [...legend].reverse();
  const legendScaleNote = percentLegendNote(selectedColumn)
    || (letterSoundDocTestTypes().includes(selectedColumn) && !isVowelTestType(selectedColumn)
      ? letterScaleNote(Object.values(latestAssessments[selectedColumn] || {}).map(r => r.totalItems))
      : null);
  const testOptions = testOptionsFor(assessment);
  const panelEmptyText = columnReady
    ? 'No students in this group.'
    : extras.status === 'failed' ? 'Could not load these results.' : 'Loading…';

  const handleVerify = async (pending) => {
    try {
      const assessmentRef = doc(db, 'assessments', pending.id);
      await updateDoc(assessmentRef, {
        status: 'completed'
      });

      await logAudit(user, 'ASSESSMENT_VERIFIED', pending.studentId, {
        assessmentId: pending.id,
        testType: pending.testType,
        quarter: pending.quarter
      });

      setPendingAssessments(prev => prev.filter(a => a.id !== pending.id));
      alert('Assessment verified and marked as completed.');

    } catch (err) {
      console.error('Error verifying assessment:', err);
      alert('Failed to verify assessment.');
    }
  };

  // Calendar-driven school-year gate (v3.29.0): when no school year is active
  // per the Academic Calendar, show the notice and no page content.
  if (!loading && schoolYearInfo && schoolYearInfo.status !== 'active') {
    return <SchoolYearNotice info={schoolYearInfo} />;
  }

  // Display labels (v3.71.0): override → shortened Clever name → raw.
  const classLabels = buildClassLabelMap(classes);

  return (
    <div style={{ padding: '20px' }}>
      <h2 style={{ textAlign: 'center', fontSize: '2em', marginBottom: '30px' }}>Teacher Dashboard</h2>
      {loading ? (
        <p>Loading data...</p>
      ) : loadError ? (
        <LoadErrorNotice error={loadError} onRetry={retryLoad} />
      ) : (
        <>
	          <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginBottom: '20px', flexWrap: 'wrap', backgroundColor: '#fff', padding: '15px', borderRadius: '8px', boxShadow: 'var(--shadow-sm)', border: '1px solid #e0e0e0' }}>
            <span style={{ fontWeight: 'bold', marginRight: '10px' }}>
              {selectedHeading} Mastery Legend:
            </span>
            {legend.map(b => (
              <div key={b.key} style={{ display: 'flex', alignItems: 'center' }}>
                <span className={`indicator-dot ${BAND_INDICATOR_CLASS[b.key]}`} style={{ margin: 0 }}></span>
                <span style={{ marginLeft: '8px', fontSize: '0.9em' }}><strong>{b.label}:</strong> {b.range}</span>
              </div>
            ))}
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span className="indicator-dot indicator-gray" style={{ margin: 0 }}></span>
              <span style={{ marginLeft: '8px', fontSize: '0.9em' }}><strong>Not Tested</strong></span>
	            </div>
            {legendScaleNote && (
              <div data-testid="legend-scale-note" className="dashboard-legend-note" style={{ flexBasis: '100%', textAlign: 'center', fontSize: '0.85em', color: '#555' }}>
                {legendScaleNote}
              </div>
            )}
	          </div>

	          {(user?.role?.admin || user?.role?.teacher || !user?.role?.ea) && pendingAssessments.length > 0 && (
            <div className="no-print" style={{ marginBottom: '30px', padding: '20px', backgroundColor: '#fff3cd', border: '1px solid #ffeeba', borderRadius: '8px' }}>
              <h3 style={{ marginTop: 0, color: '#856404' }}>Review Queue (Pending Verification)</h3>
              <p style={{ color: '#856404' }}>The following assessments were submitted by an Educational Assistant and require your verification before becoming official.</p>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: '#fff', border: '1px solid #dee2e6' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #ccc', backgroundColor: '#f8f9fa' }}>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Student</th>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Class</th>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Test Type</th>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Quarter</th>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Score</th>
                      <th style={{ padding: '12px', textAlign: 'center' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingAssessments.map(a => (
                      <tr key={a.id} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '12px' }}>{a.studentName}</td>
                        <td style={{ padding: '12px' }}>{a.classId ? classLabelFrom(classLabels, a.classId) : a.className}</td>
                        <td style={{ padding: '12px', textTransform: 'capitalize' }}>{a.testType}</td>
                        <td style={{ padding: '12px' }}>{a.quarter}</td>
                        <td style={{ padding: '12px' }}>{a.correctCount !== undefined ? a.correctCount : Object.values(a.responses || {}).filter(v => v === 'Correct').length}/{a.totalLetters || 26}</td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <button
                            onClick={() => handleVerify(a)}
                            className="btn-modern btn-modern-success"
                            style={{ padding: '6px 12px', fontSize: '0.9em' }}
                          >
                            Verify
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {classes.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginBottom: '20px', flexWrap: 'wrap' }}>
              <div className="dashboard-filter" style={{ display: 'flex', alignItems: 'center' }}>
                <label htmlFor="dashboard-assessment-filter" style={{ marginRight: '10px', fontWeight: 'bold' }}>Assessment:</label>
                <select
                  id="dashboard-assessment-filter"
                  value={assessment.id}
                  onChange={handleAssessmentChange}
                  style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '1em', minWidth: '150px', fontFamily: '"Comic Sans MS", "Chalkboard SE", "Comic Neue", sans-serif' }}
                >
                  {offeredAssessments.map(a => (
                    <option key={a.id} value={a.id}>{a.label}</option>
                  ))}
                </select>
              </div>
              <div className="dashboard-filter" style={{ display: 'flex', alignItems: 'center' }}>
                <label htmlFor="dashboard-test-filter" style={{ marginRight: '10px', fontWeight: 'bold' }}>Test Type:</label>
                <select
                  id="dashboard-test-filter"
                  value={assessment.id === LETTER_SOUND_ID ? selectedTestType : (paTest || '')}
                  onChange={assessment.id === LETTER_SOUND_ID ? handleTestTypeChange : handlePaTestChange}
                  disabled={testOptions.length === 0}
                  style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '1em', minWidth: '150px', fontFamily: '"Comic Sans MS", "Chalkboard SE", "Comic Neue", sans-serif' }}
                >
                  {/* Letters & Sounds: the shared row table (assessmentEntry.js) —
                      the letter tests, the Sounds variants while their flag is
                      on, the vowel tests while their switch is on. A PA
                      assessment: its tests. HFW / CVC / NWF: their latest
                      test, so the dropdown is greyed out. */}
                  {testOptions.length === 0
                    ? <option value="">Latest test</option>
                    : testOptions.map(row => (
                      <option key={row.key} value={row.key}>{row.label}</option>
                    ))}
                </select>
              </div>
              <div data-tour={TOUR_TARGETS.classFilter} className="dashboard-filter" style={{ display: 'flex', alignItems: 'center' }}>
                <label htmlFor="dashboard-class-filter" style={{ marginRight: '10px', fontWeight: 'bold' }}>Filter by Class:</label>
                <select
                  id="dashboard-class-filter"
                  value={selectedClassFilter}
                  onChange={(e) => setSelectedClassFilter(e.target.value)}
                  style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '1em', minWidth: '150px', fontFamily: '"Comic Sans MS", "Chalkboard SE", "Comic Neue", sans-serif' }}
                >
                  <option value="All">All Classes</option>
                  {classes.map(c => (
                    <option key={c.id} value={c.id}>{classLabelFrom(classLabels, c.id)}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
          {/* HFW / CVC read status (v3.97.0). The live region stays mounted so a
              screen reader announces "Loading…" when it appears; a failed read
              is said on EVERY view — the notes peek depends on it too. */}
          {classes.length > 0 && (
            <p role="status" aria-live="polite" className="no-print dashboard-extras-status" style={{ textAlign: 'center', color: '#555', margin: columnFromExtras && extras.status === 'loading' ? '0 0 16px' : 0 }}>
              {columnFromExtras && extras.status === 'loading' ? `Loading ${assessment.label} results…` : ''}
            </p>
          )}
          {classes.length > 0 && extras.status === 'failed' && extrasNames && (
            <div role="alert" className="no-print dashboard-extras-alert" style={{ textAlign: 'center', margin: '0 auto 16px', padding: '12px 16px', maxWidth: '640px', background: '#fff3cd', border: '1px solid #ffeeba', borderRadius: '8px', color: '#856404' }}>
              {columnFromExtras
                ? `${extrasNames} results could not be loaded. Letters & Sounds is not affected.`
                : `${extrasNames} results and notes could not be loaded, so their notes are missing below.`}{' '}
              <button type="button" className="btn-modern btn-modern-primary" onClick={retryExtras} style={{ marginLeft: '8px' }}>Retry</button>
            </div>
          )}
          <div className="dashboard-grid">
            {classes.length > 0 ? (
              classes.filter(c => selectedClassFilter === 'All' || c.id === selectedClassFilter).map((classItem, classIndex) => {
                const originalClassId = classLabelFrom(classLabels, classItem.id);
                const studentListForClass = students[classItem.id] || [];
                return (
                  <div key={classItem.id} style={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '12px',
                    padding: '20px',
                    backgroundColor: '#fff',
                    boxShadow: 'var(--shadow-md)',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <h3 style={{ marginTop: '0', borderBottom: '1px solid #eee', paddingBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>{originalClassId} - {classItem.homeroomTeacher || user.displayName}</span>
                    </h3>

                    {/* Actionable Insights Panel — one panel per band of the
                        selected test, worst first (three for most tests; the PA
                        screener's two). */}
                    <div data-tour={classIndex === 0 ? TOUR_TARGETS.bandSummary : undefined} style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '30px' }}>
                      {panelBands.map(b => {
                        const ps = PANEL_STYLES[b.key] || PANEL_STYLES.emerging;
                        const inBand = columnReady ? studentsInBand(studentListForClass, b.key) : [];
                        return (
                          <div key={b.key} style={{ flex: '1 1 250px', background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', borderLeft: `5px solid ${ps.border}`, border: '1px solid #eaeaea', borderLeftWidth: '5px' }}>
                            <h3 style={{ fontSize: '1.2rem', color: ps.heading, borderBottom: '1px solid #eaeaea', paddingBottom: '10px', marginBottom: '15px', fontFamily: '"Comic Sans MS", "Chalkboard SE", "Comic Neue", sans-serif', textTransform: 'capitalize' }}>{b.label} ({selectedHeading})</h3>
                            {inBand.map(s => {
                              const { score, totalItems } = latestAssessments[selectedColumn][s.id];
                              return (
                                <div key={s.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: ps.row, borderRadius: '8px', marginBottom: '8px' }}>
                                  <span style={{ fontWeight: 'bold', color: '#333' }}>{s.name}</span>
                                  <span style={{ background: ps.chip, color: ps.chipText, padding: '4px 10px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: ps.dot, boxShadow: '0 1px 2px rgba(0,0,0,0.3)' }}></div>
                                    {score}/{totalItems}
                                  </span>
                                </div>
                              );
                            })}
                            {inBand.length === 0 && <p style={{ color: '#6c757d', fontStyle: 'italic', fontSize: '0.9rem', margin: 0 }}>{panelEmptyText}</p>}
                          </div>
                        );
                      })}
                    </div>

                    {/* Students: up to four columns as the card's width allows
                        (three on a 1280 laptop; one or two on an iPad; one on a
                        phone — App.css); Take sits right after the name
                        (v3.97.0). */}
                    <div className="dashboard-student-grid">
                      {studentListForClass.length > 0 ? (
                        studentListForClass.map(student => (
                          <div key={student.id} className="dashboard-student-row">
                            <div className="dashboard-student-who">
                              <div
                                style={{ fontSize: '16px', display: 'flex', alignItems: 'center', color: '#0056b3', cursor: 'pointer', textDecoration: 'underline' }}
                                onClick={() => setSelectedStudentForModal({ studentId: student.id, name: student.name, classId: student.classId })}
                              >
                                {/* Its own flex item, so a long single-word name wraps
                                    inside its room instead of running under the 📝. */}
                                <span className="dashboard-student-name">{student.name}</span>
                                {(() => {
                                  const scoreData = columnReady && latestAssessments[selectedColumn] ? latestAssessments[selectedColumn][student.id] : null;
                                  const info = getIndicatorInfo(scoreData ? scoreData.score : null, selectedColumn, scoreData ? scoreData.totalItems : undefined);
                                  return <span className={`indicator-dot ${info.class}`} title={`Latest ${selectedHeading}: ${columnReady ? info.label : 'loading'} ${scoreData ? '(' + scoreData.score + '/' + scoreData.totalItems + ')' : ''}`} style={{ marginLeft: '8px' }}></span>;
                                })()}
                              </div>
                              <StudentNotesPeek studentName={student.name} groups={notesFor(student)} pendingNote={notesPendingText} />
                            </div>
                            <Link
                              to={takeHrefFor(assessment, selectedTestType, paTest)}
                              state={{ studentId: student.id, classId: classItem.id }}
                              className="btn-modern btn-modern-primary dashboard-take-btn"
                              title={takeTitleFor(assessment, selectedTestType, paTest, student.name)}
                              aria-label={takeAriaLabelFor(assessment, selectedTestType, paTest, student.name)}
                            >
                              {takeLabelFor(assessment)}
                            </Link>
                          </div>
                        ))
                      ) : (
                        <div style={{ padding: '12px 0' }}>No students found for this class.</div>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div style={{ 
                width: '100%', 
                textAlign: 'center', 
                padding: '60px 20px', 
                border: '2px dashed #94a3b8', 
                borderRadius: '12px', 
                backgroundColor: '#f8fafc' 
              }}>
                <div style={{ fontSize: '3rem', marginBottom: '20px' }}>📋</div>
                <h3 style={{ color: '#475569', marginBottom: '10px' }}>Assignment Pending</h3>
                {user?.role?.ea ? (
                  <p style={{ color: '#64748b', maxWidth: '500px', margin: '0 auto' }}>
                    You haven't been assigned to a classroom teacher yet. Once an administrator or teacher adds your email (<strong>{user.email}</strong>) to their class roster, your students will appear here.
                  </p>
                ) : (
                  <>
                    <p style={{ color: '#64748b', maxWidth: '500px', margin: '0 auto', marginBottom: '20px' }}>
                      No active classes are linked to your account.
                    </p>
                    <Link to="/student-management">
                      <button className="btn-modern btn-modern-primary">Go to Class Management</button>
                    </Link>
                  </>
                )}
              </div>
            )}
          </div>
          {classes.length > 0 && (
            <div style={{ textAlign: 'center', marginTop: '40px' }}>
              <Link to={resultsHrefFor(assessment)}>
                <button className="btn-modern btn-modern-primary" style={{ padding: '12px 24px', fontSize: '16px' }}>View Full Results</button>
              </Link>
            </div>
          )}
        </>
      )}

      {selectedStudentForModal && (
        <StudentDetailsModal
          user={user}
          student={selectedStudentForModal}
          assessments={allAssessments.filter(a => a.studentId === selectedStudentForModal.studentId)}
          letterColumns={['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z']}
          onClose={() => setSelectedStudentForModal(null)}
          onRefresh={() => {}} // Could wire a refetch here if needed, but not required for just viewing
          academicYearLabel={schoolYearInfo?.status === 'active' ? schoolYearLabel(schoolYearInfo.startYear) : ''}
        />
      )}
    </div>
  );
}

export default Dashboard;
